// Parse a .1pif text blob and turn it into ready-to-upsert Entry objects.
// Returns a summary the UI can show before confirming the import.

import { parse1pif } from './onepif-parser.js';
import { mapItem } from './onepif-mapper.js';

/**
 * @param {string} text
 * @returns {{
 *   entries: object[],
 *   unmapped: { typeName: string, title: string }[],
 *   trashedTitles: string[],
 *   attachmentTitles: string[],
 *   summary: {
 *     total: number,
 *     byType: Record<string, number>,
 *     unmapped: number,
 *     trashed: number,
 *     withAttachments: number
 *   }
 * }}
 */
export function importFrom1pif(text) {
  const raws = parse1pif(text);
  const entries = [];
  const onepifUuids = [];          // parallel to entries, holds 1P uuid for matching
  const unmapped = [];
  const trashedTitles = [];
  const attachmentTitles = [];
  const failed = [];               // records that threw while mapping

  for (const raw of raws) {
    const title = typeof raw?.title === 'string' ? raw.title : '';
    let result;
    try {
      result = mapItem(raw);
    } catch (err) {
      // One malformed record must not cost the user the other 400. Report it
      // as unmapped so the pre-import summary shows what was skipped.
      failed.push({ title, message: err?.message ?? String(err) });
      unmapped.push({ typeName: raw?.typeName ?? 'unknown', title });
      continue;
    }
    if (result.trashed) {
      trashedTitles.push(title);
      continue;
    }
    if (result.entry) {
      entries.push(result.entry);
      onepifUuids.push(result.onepifUuid);
      if (result.hasAttachments) attachmentTitles.push(title);
    } else {
      unmapped.push({ typeName: result.unmappedTypeName, title });
    }
  }

  const byType = {};
  for (const e of entries) byType[e.type] = (byType[e.type] ?? 0) + 1;

  return {
    entries,
    onepifUuids,
    unmapped,
    trashedTitles,
    attachmentTitles,
    failed,
    summary: {
      total: raws.length,
      byType,
      unmapped: unmapped.length,
      trashed: trashedTitles.length,
      withAttachments: attachmentTitles.length,
      failed: failed.length,
    },
  };
}

/**
 * Apply an importFrom1pif result to a LocalVault. Optionally attach binary
 * attachments by 1Password uuid:
 *   attachmentsByUuid = Map<onepifUuid, Array<{ name, type, bytes: Uint8Array }>>
 * Returns counts.
 */
export async function applyImport(vault, result, attachmentsByUuid = null) {
  let inserted = 0;
  let attached = 0;
  for (let i = 0; i < result.entries.length; i++) {
    const saved = await vault.upsert(result.entries[i]);
    inserted++;

    const uuid = result.onepifUuids?.[i];
    if (uuid && attachmentsByUuid) {
      const files = attachmentsByUuid.get(uuid) ?? attachmentsByUuid.get(uuid.toUpperCase()) ?? attachmentsByUuid.get(uuid.toLowerCase());
      if (Array.isArray(files)) {
        for (const f of files) {
          await vault.putAttachment(saved.id, f);
          attached++;
        }
      }
    }
  }
  return { inserted, attached, unmapped: result.unmapped.length };
}
